/**
 * Product analytics (launch item l8).
 *
 * Browser half of the signup → paid funnel. The server emits the one step only
 * it can see (Stripe-confirmed subscription) — see
 * backend/app/services/product_analytics.py.
 *
 * Opt-in like Sentry: with no `VITE_POSTHOG_KEY` at build time every function
 * here is a no-op and nothing leaves the browser. No SDK is loaded — events go
 * straight to PostHog's capture endpoint with `fetch(..., { keepalive: true })`,
 * so a navigation right after an event doesn't drop it.
 *
 * Privacy: events carry ids, plan names and sanitised paths only. Never
 * emails, names, free text or URL query strings (invite and reset links put
 * secrets there). Share/guest link tokens in the path are masked.
 */

export type FunnelEvent =
  | 'signup_completed'
  | 'email_verified'
  | 'onboarding_completed'
  | 'project_created'
  | 'invite_sent'
  | 'invite_accepted'
  | 'upgrade_prompt_shown'
  | 'checkout_started'

type Props = Record<string, string | number | boolean | null | undefined>

const KEY = import.meta.env.VITE_POSTHOG_KEY
const HOST = (import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com').replace(/\/+$/, '')
const ANON_STORAGE_KEY = 'karyva_anon_id'

export const analyticsEnabled = (): boolean => Boolean(KEY)

let distinctId: string | null = null
let orgId: string | null = null

function randomId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

function anonId(): string {
  try {
    const existing = localStorage.getItem(ANON_STORAGE_KEY)
    if (existing) return existing
    const id = randomId()
    localStorage.setItem(ANON_STORAGE_KEY, id)
    return id
  } catch {
    return randomId()
  }
}

/** Path segments that are secrets (share/guest link tokens) become ":token". */
export function sanitizePath(pathname: string): string {
  const parts = pathname.split('/')
  for (let i = 0; i < parts.length - 1; i++) {
    if (parts[i] === 'share' || parts[i] === 'guest') parts[i + 1] = ':token'
  }
  return parts.join('/') || '/'
}

function send(event: string, properties: Props | Record<string, unknown> = {}): void {
  if (!KEY) return
  const id = distinctId ?? anonId()
  const path = sanitizePath(window.location.pathname)
  const body = {
    api_key: KEY,
    event,
    distinct_id: id,
    timestamp: new Date().toISOString(),
    properties: {
      ...properties,
      $current_url: window.location.origin + path,
      $pathname: path,
      $lib: 'karyva-web',
      ...(orgId ? { $groups: { organization: orgId } } : {}),
    },
  }
  try {
    void fetch(`${HOST}/capture/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Analytics is best-effort; never surface a failure to the user.
  }
}

/** Record a funnel step. */
export function track(event: FunnelEvent, properties: Props = {}): void {
  send(event, properties)
}

/** Record a page view (call on every route change). */
export function trackPageview(): void {
  send('$pageview')
}

/**
 * Tie subsequent events to a signed-in user and their organization.
 * Merges the pre-login anonymous id so the signup funnel stays connected.
 */
export function identify(user: { id: number; organizations?: Array<{ id: number }> | null }): void {
  const nextId = `u_${user.id}`
  const firstOrg = user.organizations?.[0]
  const nextOrg = firstOrg ? String(firstOrg.id) : null
  if (distinctId === nextId && orgId === nextOrg) return
  const anon = distinctId ?? anonId()
  distinctId = nextId
  orgId = nextOrg
  send('$identify', { $anon_distinct_id: anon })
  if (nextOrg) {
    send('$groupidentify', { $group_type: 'organization', $group_key: nextOrg })
  }
}

/** Forget the signed-in user (on logout) and start a fresh anonymous id. */
export function resetAnalytics(): void {
  distinctId = null
  orgId = null
  try {
    localStorage.removeItem(ANON_STORAGE_KEY)
  } catch {
    // ignore
  }
}
